/**
 * House of Dusk · interface commune (lot 1). Contrat complet :
 * 00_AI_BRAIN/06_RELEVE_THEME/lots/lot1_fondations.md.
 * <hod-header-height> ecrit --hod-header-h (et --header-height) sur <body> (11 P20).
 * <hod-cart-count> suit shopify:cart:lines-update et le bfcache ; la ligne cadeau du palier (propriete
 * _hod_gift) n'est pas comptee (meme regle que le tiroir) ; sans detail.items, relit /cart.js.
 * <hod-sheet id> autour d'un <dialog> : showModal, piege de focus, Echap, voile, verrou de
 * defilement toujours rendu, focus rendu au declencheur ; une seule couche ouverte a la fois.
 * Delegation : [data-hod-sheet-open="id"], [data-hod-sheet-close], [data-hod-toggle][aria-controls],
 * [data-hod-search-open], details[data-hod-open-desktop].
 * API : window.HodUI = { openSheet(id, trigger), closeSheets(except), refreshCartCount() }.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const MOBILE_QUERY = '(max-width: 749px)';
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

/** @returns {{ cart_url?: string }} */
function themeRoutes() {
  // `Theme` est une constante globale posee par snippets/scripts.liquid.
  // eslint-disable-next-line no-undef
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

/** @param {Element} root */
function focusables(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.closest('[hidden], [inert]')) return false;
    return el.getClientRects().length > 0;
  });
}

/* -------------------------------------------------------------------------- */
/* <hod-header-height>                                                        */
/* -------------------------------------------------------------------------- */
class HodHeaderHeight extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#write();
    if ('ResizeObserver' in window) {
      this.#observer = new ResizeObserver(() => this.#write());
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #write() {
    const height = Math.round(this.getBoundingClientRect().height);
    if (!height) return;
    const value = `${height}px`;
    const style = document.body.style;
    if (style.getPropertyValue('--hod-header-h') === value) return;
    style.setProperty('--hod-header-h', value);
    style.setProperty('--header-height', value);
  }
}

/* -------------------------------------------------------------------------- */
/* <hod-cart-count>                                                           */
/* -------------------------------------------------------------------------- */
class HodCartCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Meme regle que le tiroir et /cart : la ligne cadeau du palier (propriete _hod_gift, posee par
   * hod-cart.js) n'est pas comptee ; la meme huile achetee compte (15/09/2026).
   * @param {Array<{quantity: number, properties?: Record<string, unknown>}>} items
   */
  #countItems(items) {
    return items.reduce((total, item) => {
      const isGift = Boolean(item.properties && item.properties._hod_gift);
      return isGift ? total : total + (Number(item.quantity) || 0);
    }, 0);
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then((result) => {
        const items = result?.detail?.items;
        if (Array.isArray(items)) {
          this.render(this.#countItems(items));
          return;
        }
        // Emetteur sans detail.items (fiche Horizon, standard actions, panier illisible) :
        // cart.totalQuantity compterait le cadeau, on relit /cart.js.
        this.refresh();
      })
      .catch(() => {
        /* l'erreur est traitee par l'emetteur */
      });
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };

  async refresh() {
    try {
      const response = await fetch(`${themeRoutes().cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      const cart = await response.json();
      this.render(this.#countItems(cart.items || []));
    } catch {
      /* hors ligne : on garde le chiffre affiche */
    }
  }

  /** @param {number} count */
  render(count) {
    const text = String(count);
    if (this.textContent !== text) this.textContent = text;
    this.dataset.count = text;
    this.dispatchEvent(new CustomEvent('hod:cart-count', { bubbles: true, detail: { count } }));
  }
}

/* -------------------------------------------------------------------------- */
/* <hod-sheet>                                                                */
/* -------------------------------------------------------------------------- */
class HodSheet extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  #opened = false;

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    const dialog = this.querySelector(':scope > dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  connectedCallback() {
    const dialog = this.dialog;
    if (!dialog) return;
    this.#dialog = dialog;
    if (!dialog.hasAttribute('aria-modal')) dialog.setAttribute('aria-modal', 'true');
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('close', this.#onClose);
    dialog.addEventListener('click', this.#onClick);
    dialog.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    const dialog = this.#dialog;
    if (dialog) {
      dialog.removeEventListener('cancel', this.#onCancel);
      dialog.removeEventListener('close', this.#onClose);
      dialog.removeEventListener('click', this.#onClick);
      dialog.removeEventListener('keydown', this.#onKeydown);
    }
    this.#dialog = null;
    // Garde-fou anti gel : une feuille retiree du DOM (editeur, morph) rend le defilement.
    this.#opened = false;
    unlockScroll(this);
  }

  /** @param {Element | null} [trigger] */
  open(trigger = null) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;

    closeSheets(this);
    closeCartDrawer();

    const active = document.activeElement;
    this.#trigger = trigger instanceof HTMLElement ? trigger : active instanceof HTMLElement ? active : null;

    lockScroll(this);
    try {
      dialog.showModal();
    } catch {
      unlockScroll(this);
      return;
    }
    this.#opened = true;
    this.#setExpanded(true);
    this.dispatchEvent(new CustomEvent('hod:sheet:open', { bubbles: true }));
  }

  close() {
    const dialog = this.dialog;
    if (dialog && dialog.open) dialog.close();
    // Menage synchrone : l'evenement natif « close » arrive en differe, et pas du tout dans
    // un onglet masque ; le verrou de defilement ne doit jamais en dependre (anti gel).
    this.#finish();
  }

  toggle(trigger = null) {
    if (this.isOpen) this.close();
    else this.open(trigger);
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    if (!this.id) return;
    const selector = `[data-hod-sheet-open="${CSS.escape(this.id)}"]`;
    for (const trigger of document.querySelectorAll(selector)) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /** Echap : meme chemin que « Close ». */
  #onCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.close();
  };

  /** Fermeture par le navigateur lui-meme (Echap sans cancel, formulaire method=dialog). */
  #onClose = () => this.#finish();

  /** Idempotent : rend le defilement, aria-expanded et le focus une seule fois. */
  #finish() {
    if (!this.#opened) return;
    this.#opened = false;
    unlockScroll(this);
    this.#setExpanded(false);
    const trigger = this.#trigger;
    this.#trigger = null;
    if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    this.dispatchEvent(new CustomEvent('hod:sheet:close', { bubbles: true }));
  }

  /** Clic sur le voile (hors du rectangle de la feuille). */
  #onClick = (/** @type {MouseEvent} */ event) => {
    const dialog = this.dialog;
    if (!dialog || event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) this.close();
  };

  /** Piege de focus : Tab et Maj+Tab bouclent dans la feuille. */
  #onKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Tab') return;
    const dialog = this.dialog;
    if (!dialog) return;
    const items = focusables(dialog);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Couches : une seule ouverte a la fois                                      */
/* -------------------------------------------------------------------------- */
/** @param {HodSheet | null} [except] */
function closeSheets(except = null) {
  for (const sheet of document.querySelectorAll('hod-sheet')) {
    if (sheet !== except && sheet instanceof HodSheet && sheet.isOpen) sheet.close();
  }
}

function closeCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && drawer.isOpen && typeof drawer.close === 'function') drawer.close();
}

function openSearch() {
  const modal = /** @type {any} */ (document.getElementById('search-modal'));
  if (modal && typeof modal.showDialog === 'function') modal.showDialog();
}

/* -------------------------------------------------------------------------- */
/* Bascules simples (hidden + aria-expanded)                                  */
/* -------------------------------------------------------------------------- */
/**
 * @param {HTMLElement} toggle
 * @param {boolean} [force]
 */
function setToggle(toggle, force) {
  const id = toggle.getAttribute('aria-controls');
  const target = id ? document.getElementById(id) : null;
  if (!target) return;
  const open = typeof force === 'boolean' ? force : target.hidden;
  target.hidden = !open;
  toggle.setAttribute('aria-expanded', String(open));
  const { labelOpen, labelClosed } = toggle.dataset;
  if (labelOpen && labelClosed) toggle.textContent = open ? labelOpen : labelClosed;
  toggle.dispatchEvent(new CustomEvent('hod:toggle', { bubbles: true, detail: { open, target } }));
}

/* -------------------------------------------------------------------------- */
/* Delegation, posee une seule fois                                           */
/* -------------------------------------------------------------------------- */
function onDocumentClick(/** @type {MouseEvent} */ event) {
  const origin = event.target instanceof Element ? event.target : null;
  if (!origin) return;

  const opener = origin.closest('[data-hod-sheet-open]');
  if (opener instanceof HTMLElement) {
    const sheet = document.getElementById(opener.dataset.hodSheetOpen || '');
    if (sheet instanceof HodSheet) {
      event.preventDefault();
      sheet.toggle(opener);
    }
    return;
  }

  const closer = origin.closest('[data-hod-sheet-close]');
  if (closer) {
    const sheet = closer.closest('hod-sheet');
    if (sheet instanceof HodSheet) {
      event.preventDefault();
      sheet.close();
    }
    return;
  }

  const search = origin.closest('[data-hod-search-open]');
  if (search) {
    event.preventDefault();
    closeSheets();
    // laisse la feuille rendre le focus et le defilement avant la seconde modale
    requestAnimationFrame(openSearch);
    return;
  }

  const toggle = origin.closest('[data-hod-toggle]');
  if (toggle instanceof HTMLElement) {
    event.preventDefault();
    setToggle(toggle);
  }
}

function onDocumentKeydown(/** @type {KeyboardEvent} */ event) {
  if (event.key !== 'Escape') return;
  for (const toggle of document.querySelectorAll('[data-hod-toggle][aria-expanded="true"]')) {
    if (!(toggle instanceof HTMLElement)) continue;
    const target = document.getElementById(toggle.getAttribute('aria-controls') || '');
    const active = document.activeElement;
    if (target && (active === toggle || (active && target.contains(active)))) {
      setToggle(toggle, false);
      toggle.focus({ preventScroll: true });
    }
  }
}

/* Volets ouverts en desktop seulement (hod-accordion, open: 'desktop') : rendus ouverts cote
   serveur, masques en mobile par hod-base.css tant que data-hod-ready manque (aucun decalage
   de mise en page), fermes ici puis marques prets. Rejoue au changement de plage (rotation). */
function syncDesktopOnlyDetails() {
  const mobile = window.matchMedia(MOBILE_QUERY).matches;
  for (const details of document.querySelectorAll('details[data-hod-open-desktop]:not([data-hod-ready])')) {
    if (mobile) details.removeAttribute('open');
    details.setAttribute('data-hod-ready', '');
  }
}

/** @param {MediaQueryListEvent} event */
function onMobileRangeChange(event) {
  for (const details of document.querySelectorAll('details[data-hod-open-desktop]')) {
    if (event.matches) details.removeAttribute('open');
    else details.setAttribute('open', '');
  }
}

if (!customElements.get('hod-header-height')) customElements.define('hod-header-height', HodHeaderHeight);
if (!customElements.get('hod-cart-count')) customElements.define('hod-cart-count', HodCartCount);
if (!customElements.get('hod-sheet')) customElements.define('hod-sheet', HodSheet);

const hodWindow = /** @type {any} */ (window);
if (!hodWindow.__hodUiBound) {
  hodWindow.__hodUiBound = true;
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
  // Ouvrir le tiroir panier ferme toute feuille (01 §2 : openCart remet navOpen a false).
  document.addEventListener('theme-drawer:open', () => closeSheets());
  syncDesktopOnlyDetails();
  document.addEventListener('shopify:section:load', syncDesktopOnlyDetails);
  window.matchMedia(MOBILE_QUERY).addEventListener('change', onMobileRangeChange);

  hodWindow.HodUI = {
    /** @param {string} id @param {Element | null} [trigger] */
    openSheet(id, trigger = null) {
      const sheet = document.getElementById(id);
      if (sheet instanceof HodSheet) sheet.open(trigger);
    },
    closeSheets,
    refreshCartCount() {
      for (const counter of document.querySelectorAll('hod-cart-count')) {
        if (counter instanceof HodCartCount) counter.refresh();
      }
    },
  };
}

// Gouttiere de defilement de .page-wrapper (hod-base.css : scrollbar-gutter stable a 990 px et
// plus). Publiee sur <html> en --hod-page-gutter pour les barres fixes en bas d'ecran, qui
// couvriraient sinon la barre de defilement (barres classiques Windows et Linux). 0 px quand la
// page defile sur le document (barres superposees de macOS et des mobiles compris).
if (!hodWindow.__hodPageGutterBound) {
  hodWindow.__hodPageGutterBound = true;
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper instanceof HTMLElement && 'ResizeObserver' in window) {
    const syncPageGutter = () => {
      const overflow = getComputedStyle(wrapper).overflowY;
      const scrolls = overflow === 'auto' || overflow === 'scroll' || overflow === 'hidden';
      const gutter = scrolls ? Math.max(0, wrapper.offsetWidth - wrapper.clientWidth) : 0;
      document.documentElement.style.setProperty('--hod-page-gutter', `${gutter}px`);
    };
    new ResizeObserver(syncPageGutter).observe(wrapper);
    syncPageGutter();
  }
}

/* -------------------------------------------------------------------------- */
/* Revelation au defilement (SPEC_V2 §1.1)                                    */
/* -------------------------------------------------------------------------- */
/*
 * Contrat de balisage, valable dans tout le theme :
 *   [data-hod-reveal]          le bloc a reveler. S'il contient des
 *                              [data-hod-reveal-item], ce sont eux qui sont
 *                              reveles, en cascade ; sinon c'est le bloc.
 *   [data-hod-reveal-item]     un enfant de la cascade (cellule, tuile, etape).
 *
 * Regles, toutes tenues ici et jamais par la CSS seule :
 *   opacite 0 a 1 et 12 px de translation, 400 ms, courbe --hod-ease ;
 *   seuil 0,12 et rootMargin '0px 0px -10% 0px' ; une seule fois ;
 *   60 ms de decalage par enfant, plafonnes a 3 crans (180 ms) ;
 *   un element deja a l'ecran au chargement (haut sous innerHeight x 0,9) n'est
 *   jamais masque : il est rendu en etat final, sans transition ;
 *   prefers-reduced-motion, absence d'IntersectionObserver ou JS en erreur :
 *   rien n'est masque, la page reste telle quelle.
 */
const REVEAL_STAGGER_MS = 60;
const REVEAL_STAGGER_MAX = 3;
const REVEAL_VIEW_RATIO = 0.9;

/** @param {Element} el */
function revealShow(el) {
  el.classList.add('hod-rv--in');
}

/**
 * Arme les blocs d'une portion de page. Rejouable : un element deja traite
 * porte data-hod-rv et n'est jamais rearme (editeur, sections rechargees).
 * @param {ParentNode} root
 * @param {IntersectionObserver} observer
 */
function revealArm(root, observer) {
  const blocks = Array.from(root.querySelectorAll('[data-hod-reveal]:not([data-hod-rv])'));
  if (root instanceof Element && root.matches('[data-hod-reveal]:not([data-hod-rv])')) blocks.unshift(root);
  for (const block of blocks) {
    block.setAttribute('data-hod-rv', '');
    const items = block.querySelectorAll('[data-hod-reveal-item]');
    const targets = items.length > 0 ? Array.from(items) : [block];
    let step = 0;
    for (const target of targets) {
      if (!(target instanceof HTMLElement)) continue;
      // Element hors flux (section repliee, onglet cache) : on n'y touche pas.
      if (target.offsetParent === null && getComputedStyle(target).position !== 'fixed') continue;
      if (target.getBoundingClientRect().top < window.innerHeight * REVEAL_VIEW_RATIO) continue;
      target.style.setProperty('--hod-rv-d', `${Math.min(step, REVEAL_STAGGER_MAX) * REVEAL_STAGGER_MS}ms`);
      target.classList.add('hod-rv');
      observer.observe(target);
      step += 1;
    }
  }
}

if (!hodWindow.__hodRevealBound && 'IntersectionObserver' in window) {
  hodWindow.__hodRevealBound = true;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!still.matches) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Un element deja depasse (ancre, retour arriere, recherche dans la page, restauration
          // de position) n'entrera jamais : il est pose sans attendre. Un element encore sous
          // l'ecran attend son tour. Rien ne peut rester masque.
          if (!entry.isIntersecting && entry.boundingClientRect.top > 0) continue;
          revealShow(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );
    revealArm(document, observer);
    // Editeur de theme : une section rechargee est rearmee, jamais dupliquee.
    document.addEventListener('shopify:section:load', (event) => {
      const target = /** @type {any} */ (event).target;
      if (target instanceof Element) revealArm(target, observer);
    });
    // L'utilisateur coupe les animations en cours de route : tout se pose.
    still.addEventListener('change', (event) => {
      if (!event.matches) return;
      observer.disconnect();
      for (const el of document.querySelectorAll('.hod-rv')) revealShow(el);
    });
  }
}
